import { AppCard } from "@/components/ui";
import { AiAssistedDisclaimer } from "@/components/shared";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { CollapsibleSection } from "../CollapsibleDashboardSection";
import { FundingTypeAnalysisSection } from "./readiness/FundingTypeAnalysisSection";
import { GapsWeaknessesCard } from "./readiness/GapsWeaknessesCard";
import { NextStepsCard } from "./readiness/NextStepsCard";
import { ReadinessAssessmentCard } from "./readiness/ReadinessAssessmentCard";
import { ReadinessCategoryCard } from "./readiness/ReadinessCategoryCard";
import { ReasoningPanel } from "./readiness/ReasoningPanel";
import { RecommendationsCard } from "./readiness/RecommendationsCard";
import { StrengthsCard } from "./readiness/StrengthsCard";
import {
  normalizeFundingRecommendationsFromApi,
  normalizeReadinessCategoriesFromApi,
} from "./readiness/normalizeReadinessApi";
import type {
  TFundingRecommendation,
  TInvestorReadinessAssessment,
  TReadinessCategory,
  TReadinessData,
} from "./readiness/types";

type TReadinessDashboardProps = {
  readinessData: TReadinessData | null | undefined;
  /** Raw API readiness_data for categories, errors, llm_usage */
  rawReadinessData?: Record<string, unknown> | null;
  message?: string | null;
  companyId?: string | null;
  sectionId?: string | null;
};

/**
 * Extract JSON object from text that may contain embedded JSON.
 * Handles cases where JSON is embedded in descriptive text.
 */
function extractJsonFromText(text: string): Record<string, unknown> | null {
  if (!text || typeof text !== "string") return null;

  // Try to find JSON object in the text (look for { ... } pattern)
  // First, try parsing the entire text as JSON
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Not valid JSON, continue to search for embedded JSON
  }

  // Look for "investor_readiness_assessment" pattern in the text
  // Find the start of the assessment object
  const assessmentStartRegex = /"investor_readiness_assessment"\s*:\s*\{/;
  const assessmentStartMatch = assessmentStartRegex.exec(text);
  if (assessmentStartMatch) {
    const startIndex = assessmentStartMatch.index + assessmentStartMatch[0].length - 1; // Position after the opening brace
    // Find the matching closing brace by counting braces
    let braceCount = 1;
    let endIndex = startIndex + 1;
    for (let i = startIndex + 1; i < text.length && braceCount > 0; i++) {
      if (text[i] === "{") braceCount++;
      if (text[i] === "}") braceCount--;
      if (braceCount === 0) {
        endIndex = i + 1;
        break;
      }
    }
    if (endIndex > startIndex) {
      try {
        const assessmentJson = text.substring(
          assessmentStartMatch.index + assessmentStartMatch[0].length - 1,
          endIndex
        );
        const parsed = JSON.parse(assessmentJson) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return { investor_readiness_assessment: parsed };
        }
      } catch {
        // If that fails, try to find the full JSON object containing it
      }
    }
  }

  // Fallback: Find the last occurrence of "{" that might be the start of the assessment JSON
  const lastBraceIndex = text.lastIndexOf("{");
  if (lastBraceIndex === -1) return null;

  // Try to extract and parse from the last brace to the end
  const jsonCandidate = text.substring(lastBraceIndex);
  try {
    const parsed = JSON.parse(jsonCandidate) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // If parsing fails, try to find a complete JSON object by matching braces
    let braceCount = 0;
    let endIndex = -1;
    for (let i = 0; i < jsonCandidate.length; i++) {
      if (jsonCandidate[i] === "{") braceCount++;
      if (jsonCandidate[i] === "}") braceCount--;
      if (braceCount === 0 && i > 0) {
        endIndex = i + 1;
        break;
      }
    }
    if (endIndex > 0) {
      try {
        const completeJson = jsonCandidate.substring(0, endIndex);
        const parsed = JSON.parse(completeJson) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        // Continue to return null
      }
    }
  }

  return null;
}

/**
 * Type guard for investor_readiness_assessment
 */
function isValidAssessment(value: unknown): value is TInvestorReadinessAssessment {
  if (!value || typeof value !== "object") return false;
  const assessment = value as Record<string, unknown>;
  return (
    typeof assessment.readiness_score === "number" ||
    typeof assessment.readiness_level === "number" ||
    typeof assessment.summary === "string" ||
    Array.isArray(assessment.strengths) ||
    Array.isArray(assessment.recommendations)
  );
}

/**
 * Canonical 0-100 readiness score for display. Prefers `readiness_score`;
 * legacy 1-5 `readiness_level` payloads map to band midpoints (10/30/50/70/90).
 */
function getReadinessScore(assessment: TInvestorReadinessAssessment): number | null {
  if (assessment.readiness_score != null) {
    return Math.max(0, Math.min(100, Math.round(assessment.readiness_score)));
  }
  if (assessment.readiness_level != null) {
    const level = Math.max(1, Math.min(5, Math.round(assessment.readiness_level)));
    return level * 20 - 10;
  }
  return null;
}

/** Small item-count chip shown next to a collapsible section title */
function SectionCountBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
      {count}
    </span>
  );
}

export function ReadinessDashboard({
  readinessData,
  rawReadinessData,
  message,
  companyId,
  sectionId,
}: TReadinessDashboardProps) {
  const [reasoningPanelCategory, setReasoningPanelCategory] = useState<{
    name: string;
    data: TReadinessCategory;
  } | null>(null);

  const rawCompanyReadiness = rawReadinessData?.company_readiness as Record<string, unknown> | undefined;
  const categoriesFromData: Record<string, TReadinessCategory> | undefined = readinessData?.categories;
  const categoriesFromRaw: Record<string, TReadinessCategory> =
    rawCompanyReadiness?.categories && typeof rawCompanyReadiness.categories === "object"
      ? normalizeReadinessCategoriesFromApi(rawCompanyReadiness.categories)
      : {};
  const categories: Record<string, TReadinessCategory> | undefined =
    categoriesFromData && Object.keys(categoriesFromData).length > 0
      ? categoriesFromData
      : Object.keys(categoriesFromRaw).length > 0
        ? categoriesFromRaw
        : undefined;
  const categoryEntries: [string, TReadinessCategory][] =
    categories && typeof categories === "object"
      ? Object.entries(categories).sort(([, a], [, b]) => (a.score ?? 0) - (b.score ?? 0))
      : [];

  const fundingFromData: TFundingRecommendation[] = readinessData?.funding_recommendations ?? [];
  const fundingRecommendations: TFundingRecommendation[] =
    fundingFromData.length > 0
      ? fundingFromData
      : normalizeFundingRecommendationsFromApi(rawCompanyReadiness?.funding_recommendations);

  // Try to extract assessment from message if readinessData is null
  let assessment: TInvestorReadinessAssessment | undefined = undefined;

  if (!readinessData?.company_readiness?.investor_readiness_assessment && message) {
    // Try to extract JSON from the message
    const extractedJson = extractJsonFromText(message);
    if (
      extractedJson?.investor_readiness_assessment &&
      isValidAssessment(extractedJson.investor_readiness_assessment)
    ) {
      assessment = extractedJson.investor_readiness_assessment;
    }
  } else if (readinessData?.company_readiness?.investor_readiness_assessment) {
    assessment = readinessData.company_readiness.investor_readiness_assessment;
  }

  // If we have a message but no assessment data could be extracted, display the message
  if (message && !assessment) {
    return (
      <AppCard className="border-primary/20 bg-primary/10 p-6">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <AlertCircle className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Assessment Status</h3>
        </div>
        <div className="prose prose-sm max-w-none">
          <div className="markdown-content text-sm leading-relaxed text-foreground">
            <ReactMarkdown>{message}</ReactMarkdown>
          </div>
        </div>
      </AppCard>
    );
  }

  // If no assessment and no categories, show empty state
  if (!assessment && categoryEntries.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-muted p-4">
        <p className="text-sm italic text-muted-foreground">No readiness assessment data available</p>
      </div>
    );
  }

  const suggestedDocuments = assessment?.next_suggested_documents_or_data ?? [];

  const hasSummaryOrLevel =
    assessment &&
    (assessment.summary != null || assessment.readiness_score != null || assessment.readiness_level != null);

  return (
    <div className="space-y-4">
      {(assessment ?? categoryEntries.length > 0) ? <AiAssistedDisclaimer context="readiness" /> : null}

      {/* Assessment summary + Readiness level: above Category scoring, no cards */}
      {hasSummaryOrLevel && assessment && (
        <section className="min-w-0 overflow-hidden border-b border-border pb-5">
          {/* 1. Readiness score – the headline metric (0-100), color-coded by band */}
          {(() => {
            const score = getReadinessScore(assessment);
            if (score == null) return null;
            const meta = [
              { label: "Not Ready", pill: "bg-red-500/10 text-red-600 ring-red-500/20", bar: "bg-red-500" },
              { label: "Early Stage", pill: "bg-orange-500/10 text-orange-600 ring-orange-500/20", bar: "bg-orange-500" },
              { label: "Developing", pill: "bg-amber-500/10 text-amber-600 ring-amber-500/20", bar: "bg-amber-500" },
              { label: "Strong", pill: "bg-primary/10 text-primary ring-primary/20", bar: "bg-primary" },
              { label: "Excellent", pill: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20", bar: "bg-emerald-500" },
            ][Math.min(4, Math.floor(score / 20))];
            return (
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Investment readiness</p>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${meta.pill}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${meta.bar}`} />
                    {meta.label}
                  </span>
                </div>
                <div className="mt-2 flex items-end gap-1.5">
                  <span className="text-4xl font-bold leading-none tabular-nums text-foreground">{score}</span>
                  <span className="pb-1 text-sm font-medium text-muted-foreground">/ 100</span>
                </div>
                <div
                  className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
                  role="meter"
                  aria-valuenow={score}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Readiness score ${score} of 100: ${meta.label}`}
                >
                  <div
                    className={`h-full rounded-full transition-[width] duration-500 ${meta.bar}`}
                    style={{ width: `${score}%` }}
                  />
                </div>
                <div className="mt-1.5 flex justify-between text-[11px] text-muted-foreground">
                  <span>Not Ready</span>
                  <span>Excellent</span>
                </div>
              </div>
            );
          })()}
          {/* 2. Assessment summary – supporting context */}
          {assessment.summary && (
            <div className="mt-4 min-w-0 rounded-lg bg-muted/50 p-3">
              <p className="min-w-0 break-words text-sm leading-relaxed text-foreground/80">{assessment.summary}</p>
            </div>
          )}
        </section>
      )}

      {/* Category scoring */}
      {categoryEntries.length > 0 && (
        <CollapsibleSection
          id="category-scoring"
          title="Category scoring"
          badge={<SectionCountBadge count={categoryEntries.length} />}
        >
          <div className="grid min-w-0 grid-cols-1 gap-2 xl:grid-cols-2">
            {categoryEntries.map(([name, cat]: [string, TReadinessCategory]) => (
              <div
                key={name}
                className="min-w-0"
              >
                <ReadinessCategoryCard
                  categoryName={name}
                  category={cat}
                  onViewReasoning={() => setReasoningPanelCategory({ name, data: cat })}
                />
              </div>
            ))}
          </div>
          {reasoningPanelCategory && (
            <ReasoningPanel
              open={!!reasoningPanelCategory}
              onOpenChange={(open) => !open && setReasoningPanelCategory(null)}
              categoryName={reasoningPanelCategory.name}
              category={reasoningPanelCategory.data}
            />
          )}
        </CollapsibleSection>
      )}

      {/* Funding Type Analysis: after Category scoring, before to-do tasks */}
      {fundingRecommendations.length > 0 && (
        <CollapsibleSection
          id="funding-type-analysis"
          title="Funding Type Analysis"
          badge={<SectionCountBadge count={fundingRecommendations.length} />}
        >
          <FundingTypeAnalysisSection recommendations={fundingRecommendations} />
        </CollapsibleSection>
      )}

      {/* Legacy assessment: Strengths, Recommendations, Gaps, etc. */}
      {assessment && (
        <>
          {/* Strengths and Recommendations Row */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <StrengthsCard strengths={assessment.strengths} />
            <RecommendationsCard recommendations={assessment.recommendations} />
          </div>

          {/* Gaps and Next Steps Row */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <GapsWeaknessesCard gapsOrWeaknesses={assessment.gaps_or_weaknesses} />
            <NextStepsCard nextSteps={assessment.next_suggested_documents_or_data} />
          </div>

          {/* Suggested Documents & Data Cards Section */}
          {suggestedDocuments.length > 0 && companyId && sectionId && (
            <div className="space-y-4">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-foreground sm:text-xl">Suggested Documents & Data</h2>
                <p className="mt-2 text-xs text-muted-foreground sm:text-sm">
                  Review assessment feedback for each suggested document and ask questions to learn more.
                </p>
              </div>
              <div className="space-y-4">
                {suggestedDocuments.map((documentName, index) => (
                  <ReadinessAssessmentCard
                    key={index}
                    documentName={documentName}
                    assessment={assessment}
                    companyId={companyId}
                    sectionId={sectionId}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
